import React from 'react';
import useStyles from './styles';
import { AccountMenu, Searchbar } from 'molecules';
import { Link, User, Logo, ThemeToggle, Avatar, Button } from '@elevator/ui';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import * as IconMDI from 'mdi-material-ui';
import { APPBAR_ROUTES, ROUTES } from 'config/Nav';
import { RouteConfig } from 'types/routes';
import clsx from 'clsx';

const DesktopAppbar = (props: DesktopAppbarPropTypes) => {
  const { classes } = useStyles();

  const {
    currentUser,
    handleSignoutClick,
    searchInput,
    handleSearchInput,
    onSubmitSearchInput,
    handleSubmitClick,
  } = props;

  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false);

  const renderNavListItem = (
    { navLinkText, to, external, target }: RouteConfig,
    index: number,
  ) => {
    return (
      <Box className={clsx(index === 0 && classes.firstNavLink)} key={index}>
        <Link
          href={to || '#'}
          external={external}
          target={target}
          underline="none"
          color="inherit"
          className={classes.headerLink}
          suppressHydrationWarning
        >
          {navLinkText}
        </Link>
      </Box>
    );
  };

  const renderNavLinks = () => {
    return (
      <Stack direction="row" className={classes.headerLeftContainer}>
        <Box className={classes.desktopLogoSlot}>
          <Logo href="/" width={145} height={45} glitch={true} />
        </Box>
        <Stack direction="row" gap={3} className={classes.navLinksContainer}>
          {APPBAR_ROUTES.map((routeConfig, index) =>
            renderNavListItem(routeConfig, index),
          )}
        </Stack>
      </Stack>
    );
  };

  const renderAccountHandler = () => {
    return (
      <Stack direction="row" gap={1} className={classes.desktopAccountHandlerContainer}>
        <Searchbar
          rootContainerClass={classes.searchBar}
          searchInput={searchInput}
          handleSearchInput={handleSearchInput}
          onSubmit={onSubmitSearchInput}
        />
        <Button
          className={classes.submitButton}
          variant={'contained'}
          onClick={handleSubmitClick}
        >
          <IconMDI.InboxArrowUp className={classes.submitIcon} />
          Submit
        </Button>
        {!currentUser && (
          <Box className={classes.loginLink}>
            <Button
              variant="text"
              color="inherit"
              size="medium"
              href={ROUTES.AUTH.LOG_IN.to || '#'}
              className={classes.headerLink}
              suppressHydrationWarning
            >
              Log In
            </Button>
          </Box>
        )}
        {currentUser && (
          <Box className={classes.desktopAccountAvatarContainer}>
            <IconButton
              aria-label="Account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              aria-expanded={accountMenuOpen}
              onClick={() => setAccountMenuOpen((prev) => !prev)}
              color="inherit"
              className={classes.avatarMenuButton}
            >
              <Avatar
                src={currentUser.avatar?.sourceUrl}
                alt={`${currentUser.preferredUsername} avatar`}
                username={currentUser.firstName}
                size="small"
              />
            </IconButton>
          </Box>
        )}
        <Box className={classes.accountHandlerFixedItem}>
          <ThemeToggle size="small" color="inherit" />
        </Box>
      </Stack>
    );
  };

  return (
    <ClickAwayListener onClickAway={() => setAccountMenuOpen(false)}>
      <Box className={classes.desktopAppbarWrapper} data-component="Appbar">
        <AppBar elevation={1} className={classes.desktopAppbar} suppressHydrationWarning>
          <Toolbar className={classes.headerToolbarContainer} suppressHydrationWarning>
            {renderNavLinks()}
            {renderAccountHandler()}
          </Toolbar>
        </AppBar>
        {currentUser && (
          <Box className={classes.accountDropdownAnchor}>
            <Collapse in={accountMenuOpen}>
              <Box className={classes.accountDropdownPanel}>
                <AccountMenu
                  currentUser={currentUser}
                  onClose={() => setAccountMenuOpen(false)}
                  handleSignoutClick={handleSignoutClick}
                  handleSubmitClick={handleSubmitClick}
                />
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

type DesktopAppbarPropTypes = {
  handleSignoutClick: () => void;
  currentUser: User | null;
  searchInput: string;
  handleSearchInput: (input: string) => void;
  onSubmitSearchInput: (event: React.FormEvent<HTMLFormElement>) => void;
  handleSubmitClick: () => void;
};

export default DesktopAppbar;
